<template>
  <!-- Sticky bar -->
  <div v-if="visible" class="bar" ref="barRef">
    <div class="bar__inner">

      <!-- Gauche : brand -->
      <div class="bar__brand">
        <span class="bar__brand-dot"></span>
        <div class="bar__brand-info">
          <span class="bar__brand-text">
            <a href="https://asso-refuge.fr" target="_blank" rel="noopener">{{ data?.title }}</a>
          </span>
          <span class="bar__brand-tagline">{{ data?.subtitle }}</span>
        </div>
      </div>

      <!-- Droite : info + don -->
      <div class="bar__right">
        <button class="bar__btn bar__btn--ghost" @click="openInfo = true" aria-haspopup="dialog">
          <i class="fa-solid fa-circle-info"></i>
          <span class="bar__btn-text">À propos</span>
        </button>
        <a class="bar__btn bar__btn--don" href="https://www.paypal.com/donate/?hosted_button_id=WU4SC35HMT9W2&lc=FR&locale.x=fr_FR" target="_blank" rel="noopener">
          <span class="don__heart">❤️</span>
          <span>Faire un don</span>
        </a>
      </div>

    </div>
  </div>

  <!-- Modal -->
  <Teleport to="body">
    <div v-if="openInfo" class="modal" role="dialog" aria-modal="true" @click.self="openInfo = false">
      <div class="modal__card">
        <div class="modal__header">
          <h3>{{ data?.modal?.title }}</h3>
          <button class="modal__close" @click="openInfo = false" aria-label="Fermer">✕</button>
        </div>
        <div class="modal__body" v-html="data?.modal?.contentHtml"></div>
        <div class="modal__footer">
          <a class="btn btn--primary" :href="data?.modal?.cta?.url">{{ data?.modal?.cta?.label }}</a>
          <button class="btn" @click="openInfo = false">OK</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from "vue";

const visible = ref(true);
const openInfo = ref(false);
const barRef = ref(null);
const data = ref(null);

function decodeHtmlEntities(str) {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
}

function cleanArticleToJsonString(article) {
  if (!article) return "";
  let s = article.replace(/<br\s*\/?>/gi, "");
  s = s.replace(/&nbsp;/gi, " ");
  s = decodeHtmlEntities(s);
  return s.trim();
}

onMounted(async () => {
  try {
    const res = await fetch("https://admin.asso-refuge.fr/getNewsDetails/1/80");
    const json = await res.json();
    const rawArticle = json?.[0]?.article;
    const cleaned = cleanArticleToJsonString(rawArticle);
    const parsed = JSON.parse(cleaned);
    if (parsed?.modal?.contentHtml) {
      parsed.modal.contentHtml = decodeHtmlEntities(parsed.modal.contentHtml);
    }
    data.value = parsed;
    document.body.classList.add("pub-bar-visible");
    await updateBodyPadding();
  } catch (e) {
    console.error("Erreur fetch / parse:", e);
    data.value = null;
  }
});

async function updateBodyPadding() {
  await nextTick();
  document.body.style.paddingBottom =
    visible.value && barRef.value ? `${barRef.value.offsetHeight || 0}px` : "";
}

watch(visible, (val) => {
  updateBodyPadding();
  document.body.classList.toggle("pub-bar-visible", val);
});
watch(openInfo, (val) => (document.body.style.overflow = val ? "hidden" : ""));
</script>



<style>
  @media (max-width: 600px) {
    body.pub-bar-visible .mobilefooter {
      bottom: 0px !important;
    }
  }
</style>

<style scoped>
/* ── Bar ── */
.bar {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 9999;
  background: linear-gradient(90deg, #0f0f0f 0%, #1a1a2e 60%, #0f0f0f 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.4);
  padding: 10px 20px;
  animation: bar-slidein 0.5s cubic-bezier(0.22, 1, 0.36, 1) both, bar-glow 4s ease-in-out 1s infinite;
}

/* Entrée depuis le bas */
@keyframes bar-slidein {
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

/* Pulsation subtile de la bordure haute */
@keyframes bar-glow {
  0%, 100% { border-top-color: rgba(211, 22, 133, 0.15); box-shadow: 0 -4px 24px rgba(0,0,0,0.4); }
  50%       { border-top-color: rgba(211, 22, 133, 0.6);  box-shadow: 0 -4px 32px rgba(211,22,133,0.2); }
}

.bar__inner {
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.bar__right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Brand ── */
.bar__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.bar__brand-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bar__brand-tagline {
  color: rgba(255, 255, 255, 0.8);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.2px;
  white-space: nowrap;
}

.bar__brand-dot {
  width: 8px;
  margin-top: 5px;
  height: 8px;
  border-radius: 50%;
  background: #D31685;
  box-shadow: 0 0 6px #D31685, 0 0 12px rgba(211, 22, 133, 0.5);
  animation: dot-pulse 2s ease-in-out infinite;
}

@keyframes dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.6; transform: scale(0.85); }
}

.bar__brand-text {
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.bar__brand-text a {
  color: inherit;
  text-decoration: none;
}

/* ── Divider ── */
.bar__divider {
  flex: 1;
  height: 1px;
}

/* ── Actions ── */
.bar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* ── Base button ── */
.bar__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  border: none;
  transition: transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
}

/* Ghost – À propos */
.bar__btn--ghost {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.bar__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}

/* Soutenir */
.bar__btn--soutenir {
  background: rgba(211, 22, 133, 0.15);
  color: #e84dac !important;
  border: 1px solid rgba(211, 22, 133, 0.35);
}

.bar__btn--soutenir:hover {
  background: rgba(211, 22, 133, 0.28);
  transform: scale(1.04);
}

/* Don */
.bar__btn--don {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #ff3d3d, #ff0040, #c8003a);
  color: #fff !important;
  box-shadow: 0 0 10px rgba(255, 30, 60, 0.45), 0 0 20px rgba(255, 30, 60, 0.2);
  animation: don-pulse 2s ease-in-out infinite;
}

.bar__btn--don::after {
  content: "";
  position: absolute;
  top: -50%;
  left: -75%;
  width: 50%;
  height: 200%;
  background: rgba(255, 255, 255, 0.18);
  transform: skewX(-20deg);
  animation: don-shine 3s ease-in-out infinite;
}

.bar__btn--don:hover {
  transform: scale(1.06);
  box-shadow: 0 0 16px rgba(255, 30, 60, 0.8), 0 0 32px rgba(255, 30, 60, 0.4);
}

.don__heart {
  display: inline-block;
  animation: don-heartbeat 1.2s ease-in-out infinite;
}

@keyframes don-pulse {
  0%, 100% { box-shadow: 0 0 10px rgba(255,30,60,0.45), 0 0 20px rgba(255,30,60,0.2); }
  50%       { box-shadow: 0 0 18px rgba(255,30,60,0.85), 0 0 36px rgba(255,30,60,0.45); }
}

@keyframes don-heartbeat {
  0%, 100% { transform: scale(1); }
  14%      { transform: scale(1.3); }
  28%      { transform: scale(1); }
  42%      { transform: scale(1.2); }
  56%      { transform: scale(1); }
}

@keyframes don-shine {
  0%        { left: -75%; }
  50%, 100% { left: 125%; }
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .bar {
    padding: 12px;
  }

  .mobilefooter {
    bottom: 30px !important;
  }

  .bar__inner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
  }

  .bar__brand {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  .bar__brand-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .bar__brand-text {
    font-size: 12px;
    font-weight: 700;
  }

  .bar__brand-tagline {
    font-size: 9px;
    width: 60vw;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    flex-basis: 100%;
    margin-left: 0px;
  }

  .bar__btn {
    padding: 3px 4px;
    font-size: 10px;
    
  }

  .bar__btn-text {
    display: none;
  }

  .bar__right {
    display: flex;
    gap: 2px;
    flex-shrink: 0;
  }

  .bar__btn--ghost {
    padding: 6px 8px;
    margin-right: 70px;
    margin-top: -20px;
    background: transparent;
    border: none;
  }

  .bar__btn--don {
    padding: 6px 8px;
    margin-top: 10px;
  }


}
</style>

<!-- Styles modale (non-scopés car Teleport -> body) -->
<style>
.modal {
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  display: grid;
  place-items: center;
  padding: 18px;
}

.modal__card {
  width: min(720px, 100%);
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}

.modal__header {
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
}

.modal__header h3 {
  margin: 0;
  font-size: 16px;
}

.modal__close {
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s;
}

.modal__close:hover { opacity: 1; }

.modal__body {
  padding: 16px;
  color: #222;
  line-height: 1.6;
  font-size: 14px;
}

.modal__footer {
  padding: 14px 16px;
  border-top: 1px solid #eee;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.btn {
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #e6e6e6;
  background: #f2f2f2;
  color: #111;
  transition: background 0.15s;
}

.btn--primary {
  background: rgb(87, 87, 219);
  border-color: rgb(87, 87, 219);
  color: #fff !important;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.btn--primary:hover { background: rgb(87, 87, 219); }
</style>
